// Gradient sky dome (also rendered into the PMREM environment used for specular reflections).

import * as THREE from "three";
import type { MapEnv } from "./mapTypes";

const VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = normalize(position);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform vec3 uTop;
uniform vec3 uHorizon;
uniform vec3 uBottom;
uniform vec3 uSunDir;
uniform vec3 uSunColor;
uniform float uSunGlow;
varying vec3 vDir;
void main() {
  vec3 d = normalize(vDir);
  float h = d.y;
  vec3 col = h >= 0.0
    ? mix(uHorizon, uTop, pow(clamp(h, 0.0, 1.0), 0.55))
    : mix(uHorizon, uBottom, pow(clamp(-h * 2.5, 0.0, 1.0), 0.8));
  float s = max(dot(d, uSunDir), 0.0);
  col += uSunColor * (pow(s, 900.0) * 7.0 + pow(s, 14.0) * 0.16) * uSunGlow;
  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

function createMaterial(sunGlow: number): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uTop: { value: new THREE.Color("#7fb2e8") },
      uHorizon: { value: new THREE.Color("#dbe6ef") },
      uBottom: { value: new THREE.Color("#8d8c88") },
      uSunDir: { value: new THREE.Vector3(0.4, 0.8, 0.3).normalize() },
      uSunColor: { value: new THREE.Color("#ffe1b5") },
      uSunGlow: { value: sunGlow },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    side: THREE.BackSide,
    depthTest: false,
    depthWrite: false,
    fog: false,
  });
}

function applyEnv(mat: THREE.ShaderMaterial, env: MapEnv): void {
  mat.uniforms.uTop.value.set(env.skyTop);
  mat.uniforms.uHorizon.value.set(env.skyHorizon);
  mat.uniforms.uBottom.value.set(env.skyBottom);
  mat.uniforms.uSunDir.value.set(env.sunDir[0], env.sunDir[1], env.sunDir[2]).normalize();
  mat.uniforms.uSunColor.value.set(env.sunColor);
}

export class Sky {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private readonly geometry = new THREE.SphereGeometry(1, 32, 16);
  private envTarget: THREE.WebGLRenderTarget | null = null;
  /** Kept between maps: a new generator would link its blur/convolution programs again on every join. */
  private pmrem: THREE.PMREMGenerator | null = null;
  /** The sun-less sky material drawn into the environment map (also kept so its program is linked once). */
  private envMaterial: THREE.ShaderMaterial | null = null;

  constructor() {
    this.material = createMaterial(1);
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = "sky";
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -1000;
  }

  setEnv(env: MapEnv): void {
    applyEnv(this.material, env);
  }

  /** Keeps the dome centred on the camera (it is drawn without depth, so only the direction matters). */
  follow(camera: THREE.Camera): void {
    this.mesh.position.copy(camera.position);
  }

  /** Renders the sky (without the sun disc) into a PMREM environment map for the current colours. */
  buildEnvironment(renderer: THREE.WebGLRenderer, env: MapEnv): THREE.Texture {
    this.disposeEnvironment();
    this.envMaterial ??= createMaterial(0);
    applyEnv(this.envMaterial, env);
    const scene = new THREE.Scene();
    const dome = new THREE.Mesh(this.geometry, this.envMaterial);
    dome.scale.setScalar(10);
    dome.frustumCulled = false;
    scene.add(dome);
    this.pmrem ??= new THREE.PMREMGenerator(renderer);
    this.envTarget = this.pmrem.fromScene(scene, 0.04, 0.1, 100);
    return this.envTarget.texture;
  }

  disposeEnvironment(): void {
    if (this.envTarget !== null) {
      this.envTarget.dispose();
      this.envTarget = null;
    }
  }

  dispose(): void {
    this.disposeEnvironment();
    this.pmrem?.dispose();
    this.pmrem = null;
    this.envMaterial?.dispose();
    this.envMaterial = null;
    this.geometry.dispose();
    this.material.dispose();
  }
}
